import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface AccountResult {
  investor: string;
  email?: string;
  password?: string;
  success: boolean;
  error?: string;
  notes?: string;
}

const CreateInvestorAccounts = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<AccountResult[]>([]);
  const { toast } = useToast();

  const createAccounts = async () => {
    setLoading(true);
    setResults([]);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-investor-accounts');
      
      if (error) {
        throw error;
      }

      if (data.success) {
        setResults(data.accounts);
        toast({
          title: "Contas criadas com sucesso!",
          description: "Todas as contas dos investidores foram configuradas.",
        });
      } else {
        throw new Error(data.error || 'Erro desconhecido');
      }
    } catch (error) {
      toast({
        title: "Erro ao criar contas",
        description: error.message || "Ocorreu um erro inesperado",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Criar Contas dos Investidores
          </CardTitle>
          <CardDescription>
            Criar contas de acesso internas para os 4 investidores com emails e senhas controladas pelo sistema.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              <p><strong>Investidores que serão criados:</strong></p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li>Leonardo Tinocco - R$ 30.000 (6,78% a.m.)</li>
                <li>Fernando Mercúrio - R$ 25.000 (7,33% a.m.)</li>
                <li>Márcio Caldas Vieira - R$ 41.500 (6,00% a.m.)</li>
                <li>Luis Vuelma - 2 carteiras: R$ 110.000 (6,38%) + R$ 21.000 (7,33%)</li>
              </ul>
            </div>
            
            <Button 
              onClick={createAccounts} 
              disabled={loading || results.length > 0}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Criando contas...
                </>
              ) : (
                <>
                  <Users className="mr-2 h-4 w-4" />
                  Criar Contas dos Investidores
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resultados da Criação</CardTitle>
            <CardDescription>
              Contas criadas e credenciais de acesso geradas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {results.map((result, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-lg border ${
                    result.success 
                      ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950' 
                      : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {result.success ? (
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h4 className="font-semibold">{result.investor}</h4>
                      {result.success ? (
                        <div className="mt-2 space-y-1 text-sm">
                          <p><strong>Email:</strong> {result.email}</p>
                          <p><strong>Senha:</strong> <code className="px-2 py-1 bg-muted rounded">{result.password}</code></p>
                          {result.notes && <p className="text-muted-foreground">{result.notes}</p>}
                        </div>
                      ) : (
                        <p className="text-red-600 text-sm mt-1">{result.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {results.some(r => r.success) && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  Instruções para os investidores:
                </h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800 dark:text-blue-200">
                  <li>Acessar o portal através da página de login</li>
                  <li>Utilizar o email e senha fornecidos acima</li>
                  <li>Após o primeiro login, podem alterar a senha se desejarem</li>
                  <li>Terão acesso apenas aos seus próprios investimentos e rendimentos</li>
                </ol>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CreateInvestorAccounts;